import i18next from 'i18next'
import * as React from 'react'
import classNames from 'classnames'
import {
    type FileExtension,
    isPhotoExtension,
    isVideoExtension,
} from '../../../shared/domain/files/fileExtensions.no-deps'
import type * as File from '../../../shared/domain/files/types.no-deps'
import type { CreateAnswer } from '../../../shared/domain/flows/answer.no-deps'
import type { LoopPathIds } from '../../../shared/domain/flows/LoopPath.no-deps'
import type * as Flow from '../../../shared/domain/flows/types.no-deps'
import type * as Guide from '../../../shared/domain/guides/types.no-deps'
import { GuideTile } from '../../../shared/webcomponents/react-wrappers/GuideTile'
import type { CommonDeps } from '../base/CommonDeps'
import { FileGuideNotFoundModal } from '../guide/FileGuideNotFoundModal'
import { FileGuideOfflineModal } from '../guide/FileGuideOfflineModal'
import { GuideParentInfo } from '../guide/GuideParentInfo'
import { MarkAsCompleteButton } from '../guide/MarkAsCompleteButton'
import type { AppFileGuideDetails, AppGuide, FileAppGuide, ParentInfo } from '../guide/Model'
import { PhotoFileGuide } from '../guide/PhotoFileGuide'
import { VideoFileGuide } from '../guide/VideoFileGuide'
import type { RenderCachedImage } from '../image/WithCachedImage'
import { SVG_Info } from '../svg/SVG_Info'
import { cancelPromiseManagers, createPromiseManager } from '../util/PromiseManager'
import { initialPromiseState, type PromiseState } from '../util/PromiseState'
import { createStateLens } from '../util/react'
import { ElementLabel } from './ElementLabel'
import { ExecutionElement } from './ExecutionElement'
import { isNil } from '../../../shared/lib/functional/nil.no-deps'

export interface Props {
    deps: CommonDeps
    elementId: Flow.ElementId
    fetchAppGuides: () => Promise<AppGuide[]>
    fetchFileGuideDetails: (fileId: File.FileId, fresh: boolean) => Promise<AppFileGuideDetails>
    label: string
    loopPathIds: LoopPathIds
    onOpenFile: (downloadUrl: string, fileExtension: FileExtension, name: string) => void
    createAnswer: CreateAnswer<Flow.GuideAnswer>
    getAnswerValue: (guide: AppGuide[]) => Partial<Flow.GuideAnswerValue[]>
    renderCachedImage: RenderCachedImage
    onClickGuideParentInfo: (parentInfo: ParentInfo) => void
    routeToGuideExecutionPage: (guideId: Guide.Id) => void
    required: boolean
    validationMessages: string[]
    allowImageZoom: boolean
    openOptions?: () => void
}

interface GuideElementContentProps extends Props {
    guide: AppGuide
    required: boolean
    answerValues: Flow.GuideAnswerValue[]
    onClick: () => Promise<void>
}

interface GuideTileProps {
    deps: CommonDeps
    guide: AppGuide
    renderCachedImage: RenderCachedImage
    onClickGuideParentInfo: (parentInfo: ParentInfo) => void
}

interface FileGuideTileProps extends GuideTileProps {
    fetchFileGuideDetails: (fileId: File.FileId, fresh: boolean) => Promise<AppFileGuideDetails>
    guide: FileAppGuide
    onOpenFile: (downloadUrl: string, fileExtension: FileExtension, name: string) => void
    allowImageZoom: boolean
}

interface GuideTileWithChildrenProps extends GuideTileProps {
    preview: boolean
    onClick?: () => void
}

interface State {
    guideState: PromiseState<AppGuide[]>
    answerValues: Flow.GuideAnswerValue[]
}

interface FileGuideTileState {
    error?: FileGuideDownloadUrlErrorType
    fileGuideDetailsState: PromiseState<AppFileGuideDetails>
}

type FileGuideDownloadUrlErrorType = 'notFound' | 'offline'

export class ElementGuide extends React.Component<Props, State> {
    public state: State = {
        guideState: initialPromiseState,
        answerValues: [],
    }

    private promiseManagers = {
        guides: createPromiseManager(() => this.props.deps, createStateLens(this, 'guideState')),
    }

    public async componentDidMount(): Promise<void> {
        const guides = await this.promiseManagers.guides.trigger(this.props.fetchAppGuides)
        const answerValues: Flow.GuideAnswerValue[] = this.props.getAnswerValue(guides) as Flow.GuideAnswerValue[]
        this.setState({ answerValues })
    }

    public componentWillUnmount(): void {
        cancelPromiseManagers(this.promiseManagers)
    }

    public createAnswer = (guide: AppGuide) => {
        const newAnswersValues = this.state.answerValues.map((answer) => {
            if (answer.guideId === guide.id) {
                return {
                    ...answer,
                    completed: !answer.completed,
                }
            }
            return answer
        })

        this.setState({ answerValues: newAnswersValues })

        return this.props.createAnswer((answer) => ({
            ...answer,
            type: 'guide',
            value: newAnswersValues,
        }))
    }

    public render(): JSX.Element {
        const renderContent = (guides: AppGuide[]) => {
            if (guides?.length === 0) {
                return <></>
            }

            return (
                <ExecutionElement
                    deps={this.props.deps}
                    elementId={this.props.elementId}
                    loopPathIds={this.props.loopPathIds}
                    validationMessages={this.props.validationMessages}
                >
                    <ElementLabel
                        label={this.props.label}
                        required={this.props.required}
                        validationMessages={[]}
                        openOptions={this.props.openOptions}
                    />
                    {guides.map((guide) => {
                        return isNil(guide) ? (
                            <GuideElementDeletedMessage />
                        ) : (
                            <GuideElementContent
                                {...this.props}
                                key={guide.id}
                                guide={guide}
                                answerValues={this.state.answerValues}
                                onClick={() => this.createAnswer(guide)}
                            />
                        )
                    })}
                </ExecutionElement>
            )
        }

        return <>{this.promiseManagers.guides.render(renderContent)}</>
    }
}

function GuideElementDeletedMessage() {
    const deletedGuideMessage = i18next.t('appcore:element.guide.deletedGuideMessage')

    return (
        <div className="AppCore__flow__ElementGuide-guide-deleted-wrapper">
            <div className="AppCore__flow__ElementGuide-guide-deleted__icon">
                <SVG_Info color="var(--red)" />
            </div>
            {deletedGuideMessage}
        </div>
    )
}

function GuideElementContent(props: GuideElementContentProps) {
    const guide = props.guide
    const tileAttrs = {
        deps: props.deps,
        fetchFileGuideDetails: props.fetchFileGuideDetails,
        onClickGuideParentInfo: props.onClickGuideParentInfo,
        renderCachedImage: props.renderCachedImage,
    }

    const renderGuideTile = () => {
        switch (guide.variant) {
            case 'file':
                return (
                    <FileGuideTile
                        {...tileAttrs}
                        guide={guide}
                        onOpenFile={props.onOpenFile}
                        allowImageZoom={props.allowImageZoom}
                        onClickGuideParentInfo={props.onClickGuideParentInfo}
                    />
                )

            case 'report':
            case 'workflow':
                return (
                    <GuideTileWithChildren
                        {...tileAttrs}
                        guide={guide}
                        onClick={() => props.routeToGuideExecutionPage(guide.id)}
                        onClickGuideParentInfo={props.onClickGuideParentInfo}
                        preview={false}
                    />
                )

            case 'url':
                return (
                    <a href={guide.url} target={'_blank'}>
                        <GuideTileWithChildren
                            {...tileAttrs}
                            guide={guide}
                            preview={false}
                            onClickGuideParentInfo={props.onClickGuideParentInfo}
                        />
                    </a>
                )
        }
    }

    const markAsCompletedValue = props.answerValues.find((answer) => answer.guideId === guide.id)?.completed || false

    const invalid: boolean =
        props.validationMessages.length &&
        props.validationMessages[0] === i18next.t('appcore:ValidationMessage.requiredAnswer') &&
        !markAsCompletedValue

    return (
        <div className="AppCore__flow__ElementGuide">
            {invalid ? <GuideWarningMessage /> : null}
            <div className={classNames('AppCore__flow__ElementGuide-guide-tile', { invalid })}>{renderGuideTile()}</div>
            {props.required && <MarkAsCompleteButton onClick={props.onClick} markedAsComplete={markAsCompletedValue} />}
        </div>
    )
}

class FileGuideTile extends React.Component<FileGuideTileProps, FileGuideTileState> {
    public state: FileGuideTileState = {
        fileGuideDetailsState: initialPromiseState,
    }

    private promiseManagers = {
        fileGuideDetails: createPromiseManager(() => this.props.deps, createStateLens(this, 'fileGuideDetailsState')),
    }

    public componentDidMount(): void {
        this.reloadData(false)
    }

    public componentWillUnmount(): void {
        cancelPromiseManagers(this.promiseManagers)
    }

    public render(): React.ReactNode {
        const fileGuide = this.props.guide
        const handleReloadDone = (fileGuideDetails: AppFileGuideDetails) => {
            // We want to immediately download the non-media files on a successful reload.
            const fileExtension = fileGuideDetails.fileExtension

            if (!isPhotoExtension(fileExtension) && !isVideoExtension(fileExtension)) {
                const fileDownloadUrl = fileGuideDetails.downloadUrl.withDefault(null).withDefault(null)

                if (fileDownloadUrl) {
                    this.handleOnOpenFile(fileDownloadUrl, fileExtension, fileGuide)
                }
            }
        }

        // Render file guide tile using provided download URL.
        // There are three cases here: (1) All fine, (2) Download URL maybe not available, (3) Download URL result not available.
        return this.promiseManagers.fileGuideDetails.render((fileGuideDetails) =>
            fileGuideDetails.downloadUrl
                .map((downloadUrlMaybe) =>
                    downloadUrlMaybe
                        .map((downloadUrl) =>
                            this.getMediaChildToRender(fileGuide, fileGuideDetails.fileExtension, downloadUrl)
                        )
                        .map(({ preview, render, onClick }) => (
                            // (1) All fine
                            // Show a guide tile with preview, if photo or video; otherwise download the file on click.
                            <GuideTileWithChildren
                                {...this.props}
                                preview={preview}
                                onClick={onClick}
                                onClickGuideParentInfo={this.props.onClickGuideParentInfo}
                            >
                                {preview && render()}
                            </GuideTileWithChildren>
                        ))
                        .withDefault(
                            // (2) Download URL maybe not available
                            // Show a guide tile without preview.
                            // If the user clicks on it, attempt to fetch the download URL again.
                            // If still unable to determine download URL, show an URL not found modal.
                            <>
                                <GuideTileWithChildren
                                    {...this.props}
                                    preview={false}
                                    onClick={() => this.reloadData(true, 'notFound', handleReloadDone)}
                                    onClickGuideParentInfo={this.props.onClickGuideParentInfo}
                                />
                                {this.renderNotFoundModal(handleReloadDone)}
                            </>
                        )
                )
                .withDefault(
                    // (3) Download URL result not available
                    // Show a guide tile without preview.
                    // If the user clicks on it, attempt to fetch the download URL again.
                    // If the user is still offline, show offline modal.
                    <>
                        <GuideTileWithChildren
                            {...this.props}
                            onClick={() => this.reloadData(true, 'offline', handleReloadDone)}
                            preview={false}
                            onClickGuideParentInfo={this.props.onClickGuideParentInfo}
                        />
                        {this.renderOfflineModal(handleReloadDone)}
                    </>
                )
        )
    }

    private renderNotFoundModal(onReloadDone: (fileGuideDetails: AppFileGuideDetails) => void): React.ReactNode {
        if (this.state.error !== 'notFound') {
            return null
        }

        return (
            <FileGuideNotFoundModal
                onBack={() => this.setState({ error: null })}
                onTryAgain={() => this.reloadData(true, 'notFound', onReloadDone)}
            />
        )
    }

    private renderOfflineModal(onReloadDone: (fileGuideDetails: AppFileGuideDetails) => void): React.ReactNode {
        if (this.state.error !== 'offline') {
            return null
        }

        return (
            <FileGuideOfflineModal
                onBack={() => this.setState({ error: null })}
                onTryAgain={() => this.reloadData(true, 'offline', onReloadDone)}
            />
        )
    }

    private getMediaChildToRender(
        fileGuide: FileAppGuide,
        fileExtension: FileExtension,
        fileDownloadUrl: string
    ): { preview: boolean; render: () => React.ReactNode; onClick?: () => void } {
        // We want to show previews for photo and video file guides.
        // We want to download the file guide if the user clicks on the guide tile.
        if (isPhotoExtension(fileExtension)) {
            return {
                preview: true,
                render: () => (
                    <PhotoFileGuide
                        deps={this.props.deps}
                        downloadUrl={fileDownloadUrl}
                        allowImageZoom={this.props.allowImageZoom}
                        slot="content"
                        tile
                    />
                ),
            }
        }

        if (isVideoExtension(fileExtension)) {
            return { preview: true, render: () => <VideoFileGuide downloadUrl={fileDownloadUrl} slot="content" /> }
        }

        return {
            preview: false,
            render: () => null,
            onClick: () => this.handleOnOpenFile(fileDownloadUrl, fileExtension, fileGuide),
        }
    }

    private handleOnOpenFile = (fileDownloadUrl: string, fileExtension: FileExtension, fileGuide: FileAppGuide) =>
        this.props.onOpenFile(fileDownloadUrl, fileExtension, fileGuide.name)

    private reloadData = (
        fresh: boolean,
        error?: FileGuideDownloadUrlErrorType,
        onDone?: (fileGuideDetails: AppFileGuideDetails) => void
    ) =>
        this.setState({ error }, async () => {
            const fileGuideDetails = await this.promiseManagers.fileGuideDetails.trigger(() =>
                this.props.fetchFileGuideDetails(this.props.guide.file, fresh)
            )

            if (onDone) {
                onDone(fileGuideDetails)
            }
        })
}

function GuideTileWithChildren(props: React.PropsWithChildren<GuideTileWithChildrenProps>) {
    const guide = props.guide

    return (
        <GuideTile
            id={guide.id}
            icon={guide.iconUrl}
            onClick={props.onClick}
            preview={props.preview}
            title={guide.name}
            disablePropagationOnExtraActionIconClick={false}
        >
            {props.children}
            {guide.parentInfo && (
                <GuideParentInfo
                    deps={props.deps}
                    parentInfo={guide.parentInfo}
                    renderCachedImage={props.renderCachedImage}
                    onClickGuideParentInfo={props.onClickGuideParentInfo}
                />
            )}
        </GuideTile>
    )
}

function GuideWarningMessage() {
    return (
        <div className="AppCore__flow__ElementGuide-warning-message-wrapper">
            <div className="AppCore__flow__ElementGuide-warning-message__icon">
                <SVG_Info color="var(--red)" />
            </div>
            {i18next.t('appcore:element.guide.markAsCompleteToContinue')}
        </div>
    )
}
